import {
  Calculation,
  CalculationType,
  DataType,
  DeviceDataGroup,
  DeviceEutGroup,
  Examination,
  Project,
  SalesQuote,
  SalesQuoteDetail,
  SalesQuoteDetailInput,
  UpdateSalesQuoteInput,
} from "graphql/types"
import { isEutGroup } from "store/typeGuards"
import { getNormName } from "./getNormName"

export const calculateCost = (cal: Calculation, numberOfRuns: number) => {
  let resultPrice = 0
  let resultCost = 0
  const factorOperatingmodes = numberOfRuns
  const roundToTwo = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100
  if (cal.calculationType === CalculationType.TIME) {
    const humanPrice = cal.humanPrice.price * Number(cal.humanPrice.amount)
    const machinePrice = cal.machinePrice.price * Number(cal.machinePrice.amount)
    const humanCost = cal.humanPrice.cost * Number(cal.humanPrice.amount)
    const machineCost = cal.machinePrice.cost * Number(cal.machinePrice.amount)
    resultPrice = humanPrice + machinePrice
    resultCost = humanCost + machineCost
  } else {
    cal.connectionPrice.forEach((price) => {
      resultPrice += price.price * Number(price.amount)
      resultCost += price.cost * Number(price.amount)
    })
  }
  const price = roundToTwo(resultPrice * factorOperatingmodes)
  const cost = roundToTwo(resultCost * factorOperatingmodes)
  const margin = roundToTwo(price - cost)
  return { price, cost, margin }
}

const eutDataString = (devData: DeviceDataGroup) => {
  const returnValue = devData.deviceData.map((item) => {
    if (item.isOperatingMode) {
      return ""
    }
    const value = Array.isArray(item.data)
      ? item.data
          .map(
            (it) =>
              `${item.dataType === DataType.FLOAT && it.unit ? "ca. " : ""}${it.value}${it.unit ? ` ${it.unit}` : ""}`
          )
          .join(", ")
      : ""
    if (value === "") {
      return ""
    }
    return `${item.name}: ${value}`
  })
  return returnValue.filter(Boolean).join(", ")
}

const eutString = (dev: DeviceEutGroup) => {
  const eutDatas = dev.deviceDataGroup.map((item) => eutDataString(item)).filter(Boolean)

  const allOperatingModes = dev.examinations
    .map((item) => item.operatingModes)
    .reduce((acc, curr) => acc.concat(curr), [])

  const operatingModes = Array.from(new Set(allOperatingModes)).join(", ")

  let result = `${dev.barcode} ${dev.name}`

  // Add intended use (description) if it exists
  if (dev.description && dev.description.trim()) {
    result += `\nIntended use: ${dev.description.trim()}`
  }

  // Add assumptions if they exist
  if (dev.assumptions && dev.assumptions.trim()) {
    result += `\nAssumptions: ${dev.assumptions.trim()}`
  }

  // Add device data groups if they exist
  if (eutDatas.length > 0) {
    eutDatas[0] = eutDatas[0].charAt(0).toUpperCase() + eutDatas[0].slice(1)
    result += `\n${eutDatas.join("\n")}`
  }

  result += `\nOperating modes: ${operatingModes}`

  return result
}

type TestSetup = {
  examinations: Examination[]
  devices: DeviceEutGroup[]
}

type GroupedDevices = {
  examination: Examination
  testSetups: TestSetup[]
}

const getGroupedDevices = (devices: DeviceEutGroup[]) => {
  const groupedMap = new Map<string, GroupedDevices>()

  devices.forEach((device) => {
    if (device.examinations.length === 0) {
      return
    }
    device.examinations.forEach((examination) => {
      // Create key based on examination name and norm id for card grouping
      const cardKey = `${examination.name}_${examination.norm.id}`
      if (!groupedMap.has(cardKey)) {
        groupedMap.set(cardKey, {
          examination: examination,
          testSetups: [],
        })
      }

      const group = groupedMap.get(cardKey)!

      // Find or create test setup for this specific examination id
      let testSetup = group.testSetups.find((ts) => ts.examinations[0]?.id === examination.id)

      if (!testSetup) {
        testSetup = {
          examinations: [examination],
          devices: [],
        }
        group.testSetups.push(testSetup)
      }

      // Add device to test setup if not already present
      if (!testSetup.devices.find((d) => d.id === device.id)) {
        testSetup.devices.push(device)
      }
    })
  })
  return Array.from(groupedMap.values())
}

export const generateProjectCalculation = (
  project: Project,
  salesQuote: SalesQuote,
  salesQuoteDetails: SalesQuoteDetail[]
): UpdateSalesQuoteInput => {
  // Validate required salesQuote properties
  if (!salesQuote.quoteNbr) {
    throw new Error("Quote Number is required for updating sales quotes")
  }

  if (!salesQuote.id) {
    throw new Error("Sales Quote ID is required for updating sales quotes")
  }

  const devices: DeviceEutGroup[] = []
  project.devices.forEach((item) => {
    if (isEutGroup(item)) {
      devices.push(item)
    }
  })

  const groupedDevices = getGroupedDevices(devices)

  const deviceText = devices.map((item) => eutString(item)).join("\n\n")

  const praeTextParts = []

  // Add project description if it exists
  if (project.description && project.description.trim()) {
    praeTextParts.push(`${project.description.trim()}`)
  }

  // Add special arrangements if they exist
  if (project.specialArrangements && project.specialArrangements.trim()) {
    praeTextParts.push(`Agreements:\n${project.specialArrangements.trim()}`)
  }

  // Add the existing content
  praeTextParts.push(`Equipment under test (EUT):\n${deviceText}`)

  if (project.plannedDate) {
    praeTextParts.push(`Earliest possible project start: ${project.plannedDate}`)
  }

  const praeText = praeTextParts.join("\n\n")

  const details: SalesQuoteDetailInput[] = []

  groupedDevices.forEach((group) => {
    group.testSetups.forEach((testSetup) => {
      const deviceBarcodeModi = testSetup.devices
        .map((device) => {
          const opModes = device.examinations
            .filter(
              (exam) =>
                exam.name === testSetup.examinations[0].name &&
                exam.norm.id === testSetup.examinations[0].norm.id
            )
            .map((exam) => exam.operatingModes.join(", "))

          return `${device.barcode} ${device.name} (${opModes})`
        })
        .join("\n")

      const inventoryItemBodyText =
        `${getNormName(testSetup.examinations[0].norm)}\n` +
        `${testSetup.examinations[0].calculation.offerText}\n` +
        `EUT:\n${deviceBarcodeModi}\n` +
        (testSetup.examinations[0].specialArrangements
          ? `Special agreements: ${testSetup.examinations[0].specialArrangements}`
          : "")

      const oldDetail = salesQuoteDetails.find((d) => d.LIMSguid === testSetup.examinations[0].id)

      // Skip if inventoryID is empty or invalid
      if (
        !testSetup.examinations[0].examinationConfig.article ||
        testSetup.examinations[0].examinationConfig.article.trim() === ""
      ) {
        console.warn(
          `Skipping examination ${testSetup.examinations[0].id} - missing inventoryID (article)`
        )
        return
      }

      details.push({
        inventoryID: testSetup.examinations[0].examinationConfig.article,
        unitPrice: calculateCost(
          testSetup.examinations[0].calculation,
          Number(testSetup.examinations[0].numberOfRuns) || 1
        ).price,
        quantity: 1,
        warehouse: testSetup.examinations[0].location.abbreviation,
        inventoryItemBody: inventoryItemBodyText,
        // description: testSetup.examinations[0].name,
        LIMSguid: testSetup.examinations[0].id,
        norm: getNormName(testSetup.examinations[0].norm),
        akkreditiert: testSetup.examinations[0].isAccredited,
        ...(oldDetail ? { id: oldDetail.id, quoteID: oldDetail.quoteID } : {}),
        quoteID: salesQuote.id,
      })
    })
  })

  const deleteDetails = salesQuoteDetails
    .filter((d) => !details.some((d2) => d2.LIMSguid === d.LIMSguid))
    .filter((i) => i.LIMSguid !== null)

  const updatedDetails = [
    ...details,
    ...deleteDetails.map((item) => ({
      id: item.id,
      delete: true,
    })),
  ]

  const createSalesQuoteDatas: UpdateSalesQuoteInput = {
    id: salesQuote.id,
    quoteNbr: salesQuote.quoteNbr,
    businessAccount: salesQuote.businessAccount,
    currency: salesQuote.currency,
    date: salesQuote.date,
    description: salesQuote.description,
    status: salesQuote.status,
    primary: true,
    textHeader: praeText,
    textFooter: "",
    details: updatedDetails,
  }

  return createSalesQuoteDatas
}
